/**
 * 音乐缓存验证器
 * 负责验证用户缓存的有效性，清理用户不再拥有的歌曲缓存
 */

import musicCache from '@/utils/musicCache';
import { extractUserIdFromCacheKey, extractSongIdFromCacheKey, isCacheKeyBelongsToUser } from '@/utils/audioMD5';
import { getMusicList } from '@/utils/musicListCache'; // 使用带缓存的版本

class CacheValidator {
  constructor() {
    this.validationInProgress = false;
    this.lastValidationTime = null;
    this.validationInterval = 24 * 60 * 60 * 1000; // 24小时验证一次
    
    // 🔥 缓存清理配置开关
    // enableAutoCleanup: 是否启用自动清理缓存（包括自己的无效缓存和其他用户的缓存）
    // - false (默认): 永久保存所有缓存，不清理任何缓存
    // - true: 自动清理无效缓存和其他用户的缓存
    this.enableAutoCleanup = false; // 默认关闭自动清理，永久保存所有缓存
    
    // enableCleanupOtherUsers: 是否清理其他用户的缓存
    // - false (默认): 不清理其他用户的缓存
    // - true: 清理其他用户的缓存
    this.enableCleanupOtherUsers = false; // 默认不清理其他用户的缓存
    
    // enableCleanupInvalidOwn: 是否清理自己的无效缓存（已删除歌曲的缓存）
    // - false (默认): 不清理自己的无效缓存，保留以防需要
    // - true: 清理自己的无效缓存
    this.enableCleanupInvalidOwn = false; // 默认不清理自己的无效缓存
  }

  /**
   * 验证指定用户的所有缓存
   * @param {string|number} userId - 用户ID
   * @param {boolean} forceValidation - 是否强制验证（忽略时间间隔）
   * @returns {Promise<Object>} 验证结果
   */
  async validateUserCaches(userId, forceValidation = false) {
    if (this.validationInProgress && !forceValidation) {
      console.log('🔍 [缓存验证] 验证正在进行中，跳过重复验证');
      return { skipped: true };
    }

    // 检查是否需要验证（时间间隔）
    if (!forceValidation && this.lastValidationTime) {
      const timeSinceLastValidation = Date.now() - this.lastValidationTime;
      if (timeSinceLastValidation < this.validationInterval) {
        console.log(`🔍 [缓存验证] 距离上次验证时间过短，跳过验证 (${Math.round(timeSinceLastValidation / 1000 / 60)}分钟前)`);
        return { skipped: true, reason: 'too_recent' };
      }
    }

    this.validationInProgress = true;
    const startTime = Date.now();
    
    try {
      console.log(`🔍 [缓存验证] 开始验证用户 ${userId} 的缓存...`);
      
      // 1. 获取所有缓存键
      const allCacheKeys = await this.getAllCacheKeys();
      console.log(`🔍 [缓存验证] 发现 ${allCacheKeys.length} 个缓存项`);

      // 2. 筛选出属于当前用户的缓存和无主缓存
      const userCacheKeys = [];
      const orphanCacheKeys = [];
      const otherUserCacheKeys = [];

      allCacheKeys.forEach(key => {
        const keyUserId = extractUserIdFromCacheKey(key);
        if (keyUserId === String(userId)) {
          userCacheKeys.push(key);
        } else if (!keyUserId) {
          // 旧格式缓存，没有用户信息
          orphanCacheKeys.push(key);
        } else {
          otherUserCacheKeys.push(key);
        }
      });

      console.log(`🔍 [缓存验证] 用户缓存: ${userCacheKeys.length}, 无主缓存: ${orphanCacheKeys.length}, 其他用户缓存: ${otherUserCacheKeys.length}`);

      // 3. 获取用户当前的歌曲列表
      const userMusicList = await this.getUserMusicList(userId);
      const validSongIds = new Set(userMusicList.map(music => String(music.id)));
      console.log(`🔍 [缓存验证] 用户拥有 ${validSongIds.size} 首歌曲`);

      // 4. 验证用户缓存（只验证，不清理）
      const invalidUserCaches = [];
      const validUserCaches = [];

      for (const cacheKey of userCacheKeys) {
        const songId = extractSongIdFromCacheKey(cacheKey);
        if (songId && validSongIds.has(songId)) {
          validUserCaches.push(cacheKey);
        } else {
          // 不立即标记为无效，保留缓存以防用户需要
          console.log(`🔍 [缓存验证] 发现可能无效的用户缓存: ${cacheKey}，但保留以防需要`);
        }
      }

      // 5. 验证无主缓存（旧格式，只验证，不清理）
      const invalidOrphanCaches = [];
      const validOrphanCaches = [];

      for (const cacheKey of orphanCacheKeys) {
        const songId = extractSongIdFromCacheKey(cacheKey);
        if (songId && validSongIds.has(songId)) {
          validOrphanCaches.push(cacheKey);
        } else {
          // 不立即标记为无效，保留缓存以防用户需要
          console.log(`🔍 [缓存验证] 发现可能无效的无主缓存: ${cacheKey}，但保留以防需要`);
        }
      }

      // 6. 根据配置决定是否清理缓存
      let cleanupResults = { cleaned: 0, failed: 0, errors: [] };
      
      if (this.enableAutoCleanup) {
        const cachesToClean = [];
        
        // 根据配置决定清理哪些缓存
        if (this.enableCleanupOtherUsers) {
          // 清理其他用户的缓存
          cachesToClean.push(...otherUserCacheKeys);
          console.log(`🔍 [缓存验证] 配置允许清理其他用户缓存: ${otherUserCacheKeys.length} 个`);
        }
        
        if (this.enableCleanupInvalidOwn) {
          // 清理自己的无效缓存（已删除歌曲的缓存）
          const invalidOwnCaches = [];
          for (const cacheKey of userCacheKeys) {
            const songId = extractSongIdFromCacheKey(cacheKey);
            if (songId && !validSongIds.has(songId)) {
              invalidOwnCaches.push(cacheKey);
            }
          }
          cachesToClean.push(...invalidOwnCaches);
          console.log(`🔍 [缓存验证] 配置允许清理自己的无效缓存: ${invalidOwnCaches.length} 个`);
        }
        
        if (cachesToClean.length > 0) {
          cleanupResults = await this.cleanupInvalidCaches(cachesToClean);
        } else {
          console.log('🔍 [缓存验证] 配置不允许清理任何缓存，保留所有缓存');
        }
      } else {
        console.log('🔍 [缓存验证] 自动清理已关闭，保留所有缓存（永久保存）');
      }

      // 7. 记录验证时间
      this.lastValidationTime = Date.now();
      const validationDuration = this.lastValidationTime - startTime;

      const result = {
        userId: userId,
        totalCaches: allCacheKeys.length,
        userCaches: {
          valid: validUserCaches.length,
          preserved: userCacheKeys.length - validUserCaches.length, // 保留的缓存数量
          validKeys: validUserCaches,
          preservedKeys: userCacheKeys.filter(key => !validUserCaches.includes(key))
        },
        orphanCaches: {
          valid: validOrphanCaches.length,
          preserved: orphanCacheKeys.length - validOrphanCaches.length, // 保留的缓存数量
          validKeys: validOrphanCaches,
          preservedKeys: orphanCacheKeys.filter(key => !validOrphanCaches.includes(key))
        },
        otherUserCaches: otherUserCacheKeys.length,
        userSongs: validSongIds.size,
        cleanupResults: cleanupResults,
        validationDuration: validationDuration,
        timestamp: this.lastValidationTime
      };

      console.log(`🔍 [缓存验证] 验证完成，用时 ${validationDuration}ms`);
      
      if (cleanupResults.cleaned > 0) {
        console.log(`🔍 [缓存验证] 清理了 ${cleanupResults.cleaned} 个缓存，保留了 ${result.userCaches.preserved + result.orphanCaches.preserved} 个用户缓存`);
      } else {
        console.log(`🔍 [缓存验证] 未清理任何缓存（配置已关闭自动清理），保留所有 ${allCacheKeys.length} 个缓存`);
      }
      
      return result;

    } catch (error) {
      console.error('🔍 [缓存验证] 验证失败:', error);
      throw error;
    } finally {
      this.validationInProgress = false;
    }
  }

  /**
   * 获取用户的音乐列表
   * @param {string|number} userId - 用户ID
   * @returns {Promise<Array>} 用户音乐列表
   */
  async getUserMusicList(userId) {
    try {
      // 调用API获取用户音乐列表
      const response = await getMusicList();
      
      // 假设API返回的数据格式为 { data: { records: [...] } }
      if (response && response.data && response.data.records) {
        return response.data.records;
      } else if (response && Array.isArray(response.data)) {
        return response.data;
      } else if (Array.isArray(response)) {
        return response;
      }
      
      console.warn('🔍 [缓存验证] 无法解析用户音乐列表响应:', response);
      return [];
    } catch (error) {
      console.error('🔍 [缓存验证] 获取用户音乐列表失败:', error);
      // 如果获取失败，返回空数组，避免误删缓存
      return [];
    }
  }

  /**
   * 清理无效缓存
   * @param {Array<string>} invalidCacheKeys - 无效的缓存键列表
   * @returns {Promise<Object>} 清理结果
   */
  async cleanupInvalidCaches(invalidCacheKeys) {
    if (invalidCacheKeys.length === 0) {
      return { cleaned: 0, failed: 0, errors: [] };
    }

    console.log(`🗑️ [缓存清理] 开始清理 ${invalidCacheKeys.length} 个无效缓存...`);
    
    let cleaned = 0;
    let failed = 0;
    const errors = [];

    for (const cacheKey of invalidCacheKeys) {
      try {
        await musicCache.delete(cacheKey);
        cleaned++;
        console.log(`🗑️ [缓存清理] 已清理: ${cacheKey}`);
      } catch (error) {
        failed++;
        errors.push({ cacheKey, error: error.message });
        console.error(`🗑️ [缓存清理] 清理失败: ${cacheKey}`, error);
      }
    }

    console.log(`🗑️ [缓存清理] 清理完成: 成功 ${cleaned}, 失败 ${failed}`);
    
    return { cleaned, failed, errors };
  }

  /**
   * 获取所有缓存键
   * @returns {Promise<Array<string>>} 缓存键列表
   */
  async getAllCacheKeys() {
    try {
      const db = await this.openDB();
      return new Promise((resolve) => {
        try {
          const tx = db.transaction(['music-files'], 'readonly');
          const store = tx.objectStore('music-files');
          const request = store.getAllKeys();
          
          request.onsuccess = () => resolve(request.result || []);
          request.onerror = (error) => {
            console.warn('🔍 [缓存验证] 获取缓存键失败:', error);
            resolve([]);
          };
        } catch (error) {
          console.warn('🔍 [缓存验证] 创建事务失败:', error);
          resolve([]);
        }
      });
    } catch (error) {
      console.warn('🔍 [缓存验证] 打开数据库失败:', error);
      return [];
    }
  }

  /**
   * 打开IndexedDB
   * @returns {Promise<IDBDatabase>} 数据库实例
   */
  async openDB() {
    return new Promise((resolve, reject) => {
      // 与各缓存服务保持一致的版本号与对象存储
      const request = indexedDB.open('music-cache-db', 8);
      
      request.onupgradeneeded = (event) => {
        const db = request.result;
        // 确保基础对象存储存在
        if (!db.objectStoreNames.contains('music-files')) {
          db.createObjectStore('music-files');
        }
        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta');
        }
        // 图集媒体缓存
        if (!db.objectStoreNames.contains('music-album')) {
          db.createObjectStore('music-album');
        }
        // 视频与分片缓存
        if (!db.objectStoreNames.contains('video-files')) {
          db.createObjectStore('video-files');
        }
        if (!db.objectStoreNames.contains('partial-video-cache')) {
          db.createObjectStore('partial-video-cache');
        }
        // 歌词缓存
        if (!db.objectStoreNames.contains('music-lrc')) {
          db.createObjectStore('music-lrc');
        }
        // 🔥 HLS 分片缓存与分片清单（v8）
        if (!db.objectStoreNames.contains('video-segments')) {
          db.createObjectStore('video-segments');
        }
        if (!db.objectStoreNames.contains('video-seg-manifest')) {
          db.createObjectStore('video-seg-manifest');
        }
        
        console.log('🔧 [缓存验证] 数据库升级完成，当前对象存储:', Array.from(db.objectStoreNames));
      };
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * 清理所有其他用户的缓存（仅保留当前用户）
   * @param {string|number} currentUserId - 当前用户ID
   * @returns {Promise<Object>} 清理结果
   */
  async cleanupOtherUserCaches(currentUserId) {
    // 🔥 检查配置开关
    if (!this.enableAutoCleanup || !this.enableCleanupOtherUsers) {
      console.log('🗑️ [缓存清理] 配置不允许清理其他用户的缓存，保留所有缓存');
      return { cleaned: 0, failed: 0, errors: [] };
    }
    
    console.log(`🗑️ [缓存清理] 开始清理其他用户的缓存，保留用户 ${currentUserId}...`);
    
    const allCacheKeys = await this.getAllCacheKeys();
    const otherUserCaches = [];

    allCacheKeys.forEach(key => {
      const keyUserId = extractUserIdFromCacheKey(key);
      if (keyUserId && keyUserId !== String(currentUserId)) {
        otherUserCaches.push(key);
      }
    });

    if (otherUserCaches.length === 0) {
      console.log('🗑️ [缓存清理] 没有其他用户的缓存需要清理');
      return { cleaned: 0, failed: 0, errors: [] };
    }

    return await this.cleanupInvalidCaches(otherUserCaches);
  }
  
  /**
   * 设置缓存清理配置
   * @param {Object} config - 配置对象
   * @param {boolean} config.enableAutoCleanup - 是否启用自动清理
   * @param {boolean} config.enableCleanupOtherUsers - 是否清理其他用户的缓存
   * @param {boolean} config.enableCleanupInvalidOwn - 是否清理自己的无效缓存
   */
  setCleanupConfig(config) {
    if (config.enableAutoCleanup !== undefined) {
      this.enableAutoCleanup = config.enableAutoCleanup;
    }
    if (config.enableCleanupOtherUsers !== undefined) {
      this.enableCleanupOtherUsers = config.enableCleanupOtherUsers;
    }
    if (config.enableCleanupInvalidOwn !== undefined) {
      this.enableCleanupInvalidOwn = config.enableCleanupInvalidOwn;
    }
    
    console.log('🔧 [缓存配置] 缓存清理配置已更新:', {
      enableAutoCleanup: this.enableAutoCleanup,
      enableCleanupOtherUsers: this.enableCleanupOtherUsers,
      enableCleanupInvalidOwn: this.enableCleanupInvalidOwn
    });
  }
  
  /**
   * 获取当前缓存清理配置
   * @returns {Object} 配置对象
   */
  getCleanupConfig() {
    return {
      enableAutoCleanup: this.enableAutoCleanup,
      enableCleanupOtherUsers: this.enableCleanupOtherUsers,
      enableCleanupInvalidOwn: this.enableCleanupInvalidOwn
    };
  }

  /**
   * 获取缓存统计信息
   * @param {string|number} userId - 用户ID
   * @returns {Promise<Object>} 统计信息
   */
  async getCacheStats(userId) {
    const allCacheKeys = await this.getAllCacheKeys();
    
    let userCaches = 0;
    let orphanCaches = 0;
    let otherUserCaches = 0;
    let totalSize = 0;

    for (const key of allCacheKeys) {
      try {
        const blob = await musicCache.get(key);
        if (blob) {
          totalSize += blob.size;
        }

        const keyUserId = extractUserIdFromCacheKey(key);
        if (keyUserId === String(userId)) {
          userCaches++;
        } else if (!keyUserId) {
          orphanCaches++;
        } else {
          otherUserCaches++;
        }
      } catch (error) {
        console.warn(`获取缓存 ${key} 信息失败:`, error);
      }
    }

    return {
      userId: userId,
      userCaches,
      orphanCaches,
      otherUserCaches,
      totalCaches: allCacheKeys.length,
      totalSize: totalSize,
      totalSizeMB: (totalSize / 1024 / 1024).toFixed(2)
    };
  }
}

// 创建全局单例
const cacheValidator = new CacheValidator();

export default cacheValidator;
